
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { message } from 'ant-design-vue'
import {
  listUsers,
  allocatePoints,
  deductPoints,
  updateUserRole,
  setUserDisabled,
  batchDisableUsers,
  AdminUser,
  UserSearchFilters,
} from '@/services/admin'
import DataTable from './DataTable.vue'

const PRESET_AMOUNTS = [10, 50, 100, 500]

function newOperationKey(prefix: string): string {
  const ts = Date.now().toString(36)
  const rand = Math.random().toString(36).slice(2, 8)
  return `${prefix}-${ts}-${rand}`
}

export default defineComponent({
  name: 'UserManagement',
  components: { DataTable },
  setup() {
    const users = ref<AdminUser[]>([])
    const loading = ref(false)
    const page = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    const searchQuery = ref('')
    const pointsMin = ref<number | undefined>(undefined)
    const pointsMax = ref<number | undefined>(undefined)

    // 多选：跨页保留选中 id，便于批量操作
    const selectedRowKeys = ref<string[]>([])
    const rowSelection = computed<Record<string, unknown>>(() => ({
      selectedRowKeys: selectedRowKeys.value,
      onChange: (keys: Array<string | number>) => {
        selectedRowKeys.value = keys.map(String)
      },
    }))

    // 充值弹窗
    const pointsModalVisible = ref(false)
    const selectedUser = ref<AdminUser | null>(null)
    const pointsAmount = ref(10)
    const pointsReason = ref('')
    const pointsSaving = ref(false)
    const pointsError = ref('')
    // Two-click confirmation arming for large top-ups (>= 500) so we avoid
    // window.confirm while still requiring explicit double confirmation.
    const confirmArmed = ref(false)
    // One operation key per modal open; reused across network retries so the
    // backend can deduplicate a flaky double-submit instead of double-charging.
    const pointsOperationKey = ref('')

    const roleTogglingUserId = ref<string | null>(null)

    // 扣减弹窗（独立）
    const deductModalVisible = ref(false)
    const deductUser = ref<AdminUser | null>(null)
    const deductAmount = ref(10)
    const deductReason = ref('')
    const deductSaving = ref(false)
    const deductError = ref('')
    const deductArmed = ref(false)
    const deductOperationKey = ref('')
    const deductPreview = computed(() => {
      if (!deductUser.value) return 0
      return deductUser.value.points - Math.floor(deductAmount.value || 0)
    })

    // 单用户禁用/启用：双击确认（禁用 window.confirm）
    const disableToggling = ref(false)
    const disableArmedId = ref<string | null>(null)
    let disableArmedTimer: ReturnType<typeof setTimeout> | null = null

    // 批量禁用/启用：双击确认
    const batchOperating = ref(false)
    const batchArmed = ref(false)
    const batchArmedKind = ref<'disable' | 'enable' | null>(null)
    let batchArmedTimer: ReturnType<typeof setTimeout> | null = null

    const columns = [
      { title: '邮箱', key: 'emailOrUsername' },
      { title: '昵称', dataIndex: 'nickname', key: 'nickname' },
      { title: '积分', dataIndex: 'points', key: 'points' },
      { title: '角色', key: 'role' },
      { title: '状态', key: 'status' },
      { title: '注册时间', dataIndex: 'created_at', key: 'created_at' },
      { title: '操作', key: 'action' },
    ]

    const loadUsers = async () => {
      loading.value = true
      try {
        const filters: UserSearchFilters = {}
        if (searchQuery.value.trim()) filters.q = searchQuery.value.trim()
        if (pointsMin.value !== undefined) filters.pointsMin = pointsMin.value
        if (pointsMax.value !== undefined) filters.pointsMax = pointsMax.value
        const result = await listUsers(page.value, pageSize.value, filters)
        users.value = result.list
        total.value = result.total
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.error('load users failed', error)
        message.error('加载用户数据失败')
      }
      finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      page.value = 1
      loadUsers()
    }

    const handleTableChange = (newPage: number, newPageSize: number) => {
      page.value = newPage
      pageSize.value = newPageSize
      loadUsers()
    }

    const openPointsModal = (user: AdminUser) => {
      selectedUser.value = user
      pointsAmount.value = 10
      pointsReason.value = ''
      pointsError.value = ''
      confirmArmed.value = false
      pointsOperationKey.value = newOperationKey('topup')
      pointsModalVisible.value = true
    }

    const savePoints = async () => {
      if (!selectedUser.value) return
      const amount = Math.floor(pointsAmount.value)
      if (!amount || amount < 1) {
        pointsError.value = '请输入正整数额度'
        return
      }
      if (!pointsReason.value.trim()) {
        pointsError.value = '请填写充值原因'
        return
      }
      if (amount >= 500 && !confirmArmed.value) {
        confirmArmed.value = true
        return
      }
      pointsSaving.value = true
      pointsError.value = ''
      try {
        await allocatePoints(
          selectedUser.value.id,
          amount,
          pointsReason.value.trim(),
          pointsOperationKey.value,
        )
        pointsModalVisible.value = false
        await loadUsers()
      }
      catch (error) {
        pointsError.value = error instanceof Error ? error.message : '操作失败'
      }
      finally {
        pointsSaving.value = false
      }
    }

    // Reset the arming state whenever the amount or user changes so a stale
    // "confirm" cannot be applied to a different top-up value.
    watch(pointsAmount, () => {
      confirmArmed.value = false
    })

    const openDeductModal = (user: AdminUser) => {
      deductUser.value = user
      deductAmount.value = 10
      deductReason.value = ''
      deductError.value = ''
      deductArmed.value = false
      deductOperationKey.value = newOperationKey('deduct')
      deductModalVisible.value = true
    }

    const saveDeduct = async () => {
      if (!deductUser.value) return
      const amount = Math.floor(deductAmount.value)
      if (!amount || amount < 1) {
        deductError.value = '请输入正整数扣减额度'
        return
      }
      if (!deductReason.value.trim()) {
        deductError.value = '请填写扣减原因'
        return
      }
      if (deductPreview.value < 0) {
        deductError.value = '扣减后余额不能为负'
        return
      }
      if (amount >= 500 && !deductArmed.value) {
        deductArmed.value = true
        return
      }
      deductSaving.value = true
      deductError.value = ''
      try {
        await deductPoints(
          deductUser.value.id,
          amount,
          deductReason.value.trim(),
          deductOperationKey.value,
        )
        deductModalVisible.value = false
        await loadUsers()
      }
      catch (error) {
        deductError.value = error instanceof Error ? error.message : '操作失败'
      }
      finally {
        deductSaving.value = false
      }
    }

    watch(deductAmount, () => {
      deductArmed.value = false
    })

    const clearDisableTimer = () => {
      if (disableArmedTimer) {
        clearTimeout(disableArmedTimer)
        disableArmedTimer = null
      }
    }

    const toggleDisable = async (user: AdminUser) => {
      if (user.role === 'admin') return
      // 双击确认：首次点击 arm（3 秒内有效），再次点击执行
      if (disableArmedId.value === user.id) {
        clearDisableTimer()
        disableArmedId.value = null
        disableToggling.value = true
        try {
          await setUserDisabled(user.id, !user.is_disabled)
          await loadUsers()
        }
        catch (error) {
          // eslint-disable-next-line no-console
          console.error('toggle disable failed', error)
          message.error('状态修改失败，请刷新后重试')
        }
        finally {
          disableToggling.value = false
        }
        return
      }
      clearDisableTimer()
      disableArmedId.value = user.id
      disableArmedTimer = setTimeout(() => {
        disableArmedId.value = null
      }, 3000)
    }

    const clearBatchTimer = () => {
      if (batchArmedTimer) {
        clearTimeout(batchArmedTimer)
        batchArmedTimer = null
      }
    }

    const runBatch = async (disable: boolean) => {
      if (selectedRowKeys.value.length === 0 || batchOperating.value) return
      const kind = disable ? 'disable' : 'enable'
      // 双击确认：首次点击 arm（3 秒内有效），再次点击执行
      if (batchArmed.value && batchArmedKind.value === kind) {
        clearBatchTimer()
        batchArmed.value = false
        batchArmedKind.value = null
        batchOperating.value = true
        try {
          const res = await batchDisableUsers(selectedRowKeys.value, disable)
          const tip = [`已${disable ? '禁用' : '启用'} ${res.updated} 个用户`]
          if (res.skipped_admins > 0) tip.push(`跳过 ${res.skipped_admins} 个管理员`)
          message.success(tip.join('，'))
          selectedRowKeys.value = []
          await loadUsers()
        }
        catch (error) {
          // eslint-disable-next-line no-console
          console.error('batch disable failed', error)
          message.error('批量操作失败，请刷新后重试')
        }
        finally {
          batchOperating.value = false
        }
        return
      }
      clearBatchTimer()
      batchArmed.value = true
      batchArmedKind.value = kind
      batchArmedTimer = setTimeout(() => {
        batchArmed.value = false
        batchArmedKind.value = null
      }, 3000)
    }

    const toggleRole = async (user: AdminUser) => {
      const newRole = user.role === 'admin' ? 'user' : 'admin'
      roleTogglingUserId.value = user.id
      try {
        await updateUserRole(user.id, newRole)
        await loadUsers()
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.error('toggle role failed', error)
        message.error('角色修改失败，请刷新后重试')
      }
      finally {
        roleTogglingUserId.value = null
      }
    }

    onMounted(() => {
      loadUsers()
    })

    onBeforeUnmount(() => {
      clearDisableTimer()
      clearBatchTimer()
    })

    return {
      users,
      loading,
      page,
      pageSize,
      total,
      searchQuery,
      pointsMin,
      pointsMax,
      handleSearch,
      columns,
      handleTableChange,
      selectedRowKeys,
      rowSelection,
      pointsModalVisible,
      selectedUser,
      pointsAmount,
      pointsReason,
      pointsSaving,
      pointsError,
      confirmArmed,
      presetAmounts: PRESET_AMOUNTS,
      openPointsModal,
      savePoints,
      toggleRole,
      roleTogglingUserId,
      deductModalVisible,
      deductUser,
      deductAmount,
      deductReason,
      deductSaving,
      deductError,
      deductArmed,
      deductPreview,
      openDeductModal,
      saveDeduct,
      disableToggling,
      disableArmedId,
      toggleDisable,
      batchOperating,
      batchArmed,
      batchArmedKind,
      runBatch,
    }
  },
})
